import { DotsThreeIcon } from '@phosphor-icons/react';
import { useEffect, useId, useRef, useState } from 'react';

import { usePracticeView } from '@pages/PracticePage/model';
import { cn } from '@shared/lib/utils';

interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (nextValue: boolean) => void;
}

const ToggleRow = ({ label, checked, onChange }: ToggleRowProps) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-slate-50"
      onClick={() => onChange(!checked)}
    >
      <span>{label}</span>
      <span
        className={cn(
          'relative inline-flex h-5 w-9 items-center rounded-full border transition-colors',
          checked ? 'border-blue-500 bg-blue-500' : 'border-slate-200 bg-slate-100'
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform',
            checked ? 'translate-x-4' : 'translate-x-1'
          )}
        />
      </span>
    </button>
  );
};

export const PracticeViewDash = () => {
  const { showDescriptions, setShowDescriptions, groupByTopic, setGroupByTopic } =
    usePracticeView();
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      panelRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center justify-center rounded-sm border bg-white p-2 text-foreground shadow-sm transition-colors hover:bg-slate-50"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <DotsThreeIcon className="h-4 w-4 text-muted-foreground" />
      </button>

      {isOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-20 cursor-default"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
          />
          <div
            id={panelId}
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-label="View options"
            className="absolute right-0 z-30 mt-2 w-52 rounded-xl border border-border/70 bg-white p-2 shadow-lg"
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setIsOpen(false);
              }
            }}
          >
            <ToggleRow
              label="Show descriptions"
              checked={showDescriptions}
              onChange={setShowDescriptions}
            />
            <ToggleRow label="Group by topic" checked={groupByTopic} onChange={setGroupByTopic} />
          </div>
        </>
      ) : null}
    </div>
  );
};
